import { z } from 'zod';

import {
  type AudienceContent,
  type AudienceFilter,
  type AudienceLimitations,
  type AudienceScreener,
  type AudienceScreenerOption,
  type AudienceRange,
  COUNTRIES,
  type Country,
  type PipelineConstraint,
  type TargetingGap,
  MAX_TARGETING_GAP_CHARS,
  audienceFilterModeSchema,
  audienceRangeSchema,
  countrySchema,
} from './artifacts.ts';
import { type ArtifactChangeNotes, reconciledVersionText } from './change-notes.ts';
import { DomainError } from './errors.ts';
import { assertMeetsQualityFloor } from './quality.ts';
import type { ArtifactVersionReason } from './version-reasons.ts';

/**
 * Policy bounds on screening questions: few of them, each a single choice among a handful of
 * options, each a sentence or a short phrase. Each one adds a poll every participant answers, which
 * lengthens the study and raises its price. Product owns all of them.
 */
export const MAX_AUDIENCE_SCREENERS = 3;
export const MIN_SCREENER_OPTIONS = 2;
export const MAX_SCREENER_OPTIONS = 6;
export const MAX_SCREENER_TEXT_CHARS = 200;
export const MAX_SCREENER_OPTION_CHARS = 100;

/** The longest plain-language description of who the study hears from. */
export const MAX_AUDIENCE_DESCRIPTION_CHARS = 500;

/** The longest note a reshape carries: what the researcher asked for, in a sentence or two. */
export const MAX_AUDIENCE_NOTE_CHARS = 500;

/** The most changes one reshape carries: far above any one request, and a bound on a runaway. */
export const MAX_AUDIENCE_RESHAPE_OPS = 50;

/** The one language participants take part in; the conversion allows no other. */
export const AUDIENCE_LANGUAGE = 'English';

/** How each country reads in a sentence. */
export const COUNTRY_NAMES: Record<Country, string> = {
  UK: 'the United Kingdom',
  US: 'the United States',
};

/** The language Limitation, as every audience states it. */
export const LANGUAGE_LIMITATION = 'Participants take part in English only.';

/**
 * The Limitation an audience carries wherever its participants are generated rather than recruited.
 * Fixed text, because it is the one sentence a reader could be misled by: it is a fact about how the
 * study is fielded, not a doubt about the targeting.
 */
export const SIMULATED_PARTICIPANTS_LIMITATION =
  'Participants in this environment are simulated; they are generated to match this audience, not recruited from a panel.';

/** The countries as a sentence names them: the United States first when both are chosen. */
export const countryNamesInReadingOrder = (countries: readonly Country[]): string[] =>
  countries
    .toSorted((left, right) => (left === 'US' ? -1 : right === 'US' ? 1 : 0))
    .map((country) => COUNTRY_NAMES[country]);

/** The countries Limitation for a set of countries. */
export const countriesLimitation = (countries: readonly Country[]): string =>
  `Participants live in ${countryNamesInReadingOrder(countries).join(' or ')}.`;

/**
 * What the pipeline imposes on who can take part, in a fixed order: the language, the countries
 * chosen, and — where participants are generated — that they are. Derived, never authored: the store
 * writes this onto every audience version, whatever the writer sent, so the wording is constant text
 * and the countries, and a recruit environment never states the simulated one.
 */
export const pipelineConstraints = ({
  countries,
  participantsSimulated,
}: {
  countries: readonly Country[];
  participantsSimulated: boolean;
}): PipelineConstraint[] => [
  { key: 'language', text: LANGUAGE_LIMITATION },
  { key: 'countries', text: countriesLimitation(countries) },
  ...(participantsSimulated
    ? [{ key: 'simulated_participants' as const, text: SIMULATED_PARTICIPANTS_LIMITATION }]
    : []),
];

/** The audience with its pipeline constraints derived; everything else carried as it is. */
export const withPipelineConstraints = (
  content: AudienceContent,
  participantsSimulated: boolean,
): AudienceContent => ({
  ...content,
  limitations: {
    targeting_gaps: content.limitations.targeting_gaps,
    pipeline_constraints: pipelineConstraints({
      countries: content.countries,
      participantsSimulated,
    }),
  },
});

/**
 * A screener's or option's wording as two wordings are compared for being the same: trimmed, case
 * folded and with runs of whitespace collapsed. Remesh refuses two that match this way, so every
 * rule about distinct wording, here and at the conversion, compares through this one function.
 */
export const foldScreenerText = (text: string): string =>
  text.trim().replace(/\s+/g, ' ').toLowerCase();

const screenerInvalid = (screenerId: string | null, message: string, reason: string): DomainError =>
  new DomainError('audience_screener_invalid', message, { screener_id: screenerId, reason });

/**
 * Refuses a screening question that cannot screen. Single-select, so it needs at least two options
 * worded differently (Remesh refuses two that match once case is ignored); and at least one option
 * that ends the survey and one that does not, because a question that turns nobody away screens
 * nobody and one that turns everybody away excludes everyone. Answers to different screeners are
 * independent, so an audience excludes everyone exactly when one of its screeners does: this rule,
 * asked of each, is the whole of that check.
 */
export const assertScreenerStructure = (screener: {
  id?: string | undefined;
  text: string;
  options: readonly { text: string; terminates: boolean }[];
}): void => {
  const id = screener.id ?? null;

  if (screener.text.trim().length > MAX_SCREENER_TEXT_CHARS) {
    throw screenerInvalid(
      id,
      `A screening question is at most ${String(MAX_SCREENER_TEXT_CHARS)} characters.`,
      'question_too_long',
    );
  }

  if (
    screener.options.length < MIN_SCREENER_OPTIONS ||
    screener.options.length > MAX_SCREENER_OPTIONS
  ) {
    throw screenerInvalid(
      id,
      `A screening question has between ${String(MIN_SCREENER_OPTIONS)} and ${String(MAX_SCREENER_OPTIONS)} options to choose from.`,
      'option_count',
    );
  }

  if (screener.options.some((option) => option.text.trim().length > MAX_SCREENER_OPTION_CHARS)) {
    throw screenerInvalid(
      id,
      `A screening option is at most ${String(MAX_SCREENER_OPTION_CHARS)} characters.`,
      'option_too_long',
    );
  }

  if (
    new Set(screener.options.map((option) => foldScreenerText(option.text))).size !==
    screener.options.length
  ) {
    throw screenerInvalid(
      id,
      'Two options of one screening question are worded the same; each needs its own wording.',
      'duplicate_options',
    );
  }

  if (!screener.options.some((option) => option.terminates)) {
    throw screenerInvalid(
      id,
      'A screening question needs an option that ends the survey; one that turns nobody away screens nobody.',
      'none_terminate',
    );
  }

  if (screener.options.every((option) => option.terminates)) {
    throw screenerInvalid(
      id,
      'A screening question needs an option that lets people in; one where every answer ends the survey excludes everyone.',
      'all_terminate',
    );
  }
};

/**
 * Refuses an audience carrying more screeners than policy allows, one that cannot screen, or two
 * screeners whose questions are worded the same once folded (`foldScreenerText`).
 */
export const assertAudienceStructure = (content: {
  screeners: readonly AudienceScreener[];
}): void => {
  if (content.screeners.length > MAX_AUDIENCE_SCREENERS) {
    throw new DomainError(
      'audience_too_many_screeners',
      `An audience asks at most ${String(MAX_AUDIENCE_SCREENERS)} screening questions; each one adds time to every participant's study and to its price.`,
      { max: MAX_AUDIENCE_SCREENERS, requested: content.screeners.length },
    );
  }

  for (const screener of content.screeners) {
    assertScreenerStructure(screener);
  }

  const seen = new Set<string>();
  for (const screener of content.screeners) {
    const question = foldScreenerText(screener.text);
    if (seen.has(question)) {
      throw screenerInvalid(
        screener.id,
        'Two screening questions are worded the same; each needs its own wording.',
        'duplicate_questions',
      );
    }
    seen.add(question);
  }
};

/** Every rule an audience is held to on every write: its screeners' structure, then the floor. */
export const assertAudienceWritable = (content: AudienceContent): void => {
  assertAudienceStructure(content);
  assertMeetsQualityFloor(content);
};

const invalidAudience = (message: string, detail: Record<string, unknown> = {}): DomainError =>
  new DomainError('invalid_content', message, { kind: 'audience', ...detail });

const screenerNotFound = (screenerId: string): DomainError =>
  new DomainError(
    'audience_screener_not_found',
    'That screening question is not in this audience.',
    {
      screener_id: screenerId,
    },
  );

const filterNotFound = (filterId: string): DomainError =>
  new DomainError('audience_filter_not_found', 'That filter is not in this audience.', {
    filter_id: filterId,
  });

const limitationNotFound = (limitationId: string): DomainError =>
  new DomainError(
    'audience_limitation_not_found',
    'That Limitation is not one of the targeting gaps this audience records.',
    { limitation_id: limitationId },
  );

const words = (max: number) => z.string().trim().min(1).max(max);

/** One option of a reworded screener: which option, and its new wording. */
export const screenerOptionRewordSchema = z.strictObject({
  id: z.uuid(),
  text: z.string(),
});
export type ScreenerOptionReword = z.infer<typeof screenerOptionRewordSchema>;

/**
 * The researcher's rewording of one screening question: its text and, if given, the text of the
 * named options replaced, the question marked as their wording, and everything else — every other
 * screener, this one's option ids, order, count and which of them end the survey, and the
 * Limitations — left as it was.
 *
 * Wording only, by construction: nothing here can add, remove, reorder or change what an option
 * does, so structure cannot change through a wording edit. `options` must name exactly the stored
 * options, in their stored order. The same wording answers the content unchanged, which the store
 * stores as nothing.
 */
export const planScreenerReword = ({
  current,
  screenerId,
  text,
  options,
}: {
  current: AudienceContent;
  screenerId: string;
  text: string;
  options?: readonly ScreenerOptionReword[] | undefined;
}): AudienceContent => {
  const parsedText = words(MAX_SCREENER_TEXT_CHARS).safeParse(text);
  if (!parsedText.success) {
    throw invalidAudience(
      `A screening question is at most ${String(MAX_SCREENER_TEXT_CHARS)} characters, and not empty.`,
      { issues: z.prettifyError(parsedText.error) },
    );
  }

  const optionTexts = (options ?? []).map((option) =>
    words(MAX_SCREENER_OPTION_CHARS).safeParse(option.text),
  );
  const badOption = optionTexts.find((parsed) => !parsed.success);
  if (badOption !== undefined && !badOption.success) {
    throw invalidAudience(
      `A screening option is at most ${String(MAX_SCREENER_OPTION_CHARS)} characters, and not empty.`,
      { issues: z.prettifyError(badOption.error) },
    );
  }

  const target = current.screeners.find((screener) => screener.id === screenerId);
  if (target === undefined) {
    throw screenerNotFound(screenerId);
  }

  if (options !== undefined) {
    const sameSet =
      options.length === target.options.length &&
      options.every((option, index) => option.id === target.options[index]?.id);
    if (!sameSet) {
      throw invalidAudience(
        'Only the wording of a screening question’s options can be edited here, not which options it has or their order.',
        { screener_id: screenerId },
      );
    }
  }

  const nextOptions: AudienceScreenerOption[] = target.options.map((option, index) => {
    const reworded = optionTexts[index];

    return reworded?.success === true ? { ...option, text: reworded.data } : option;
  });

  if (
    new Set(nextOptions.map((option) => foldScreenerText(option.text))).size !== nextOptions.length
  ) {
    throw invalidAudience(
      'Two options of one screening question are worded the same; each needs its own wording.',
      { screener_id: screenerId },
    );
  }

  const unchanged =
    target.text === parsedText.data &&
    nextOptions.every((option, index) => option.text === target.options[index]?.text);
  if (unchanged) {
    return current;
  }

  const reworded: AudienceScreener = {
    ...target,
    text: parsedText.data,
    options: nextOptions,
    worded_by: 'user',
  };

  return {
    ...current,
    screeners: current.screeners.map((screener) =>
      screener.id === screenerId ? reworded : screener,
    ),
  };
};

const screenerOptionDraftSchema = z.strictObject({
  text: words(MAX_SCREENER_OPTION_CHARS),
  terminates: z.boolean(),
});

/**
 * One change a reshape makes, applied in order:
 *
 * - `set_count`, `set_countries` and `set_description` replace N, the countries and who the study
 *   hears from;
 * - `add_filter`, `change_filter` and `remove_filter` add a panel filter, change its mode or what
 *   it selects, and take it out. A filter selects options (`selected_options`) or a span
 *   (`range`): `add_filter` gives exactly one, and `change_filter` at most one, which replaces
 *   whichever the filter held;
 * - `add_screener` adds a screening question in Sigora's words; `recast_screener` replaces its
 *   options — their wording, order and which end the survey — and keeps its question, and the id of
 *   any option whose wording is unchanged; `remove_screener` takes it out;
 * - `add_limitation` and `remove_limitation` add and remove a targeting gap.
 */
export const audienceReshapeOpSchema = z.discriminatedUnion('op', [
  z.strictObject({ op: z.literal('set_count'), target_completes: z.int().positive() }),
  z.strictObject({
    op: z.literal('set_countries'),
    countries: z.array(countrySchema).min(1).max(COUNTRIES.length),
  }),
  z.strictObject({
    op: z.literal('set_description'),
    text: words(MAX_AUDIENCE_DESCRIPTION_CHARS),
  }),
  z
    .strictObject({
      op: z.literal('add_filter'),
      dimension: z.string().trim().min(1),
      mode: audienceFilterModeSchema,
      selected_options: z.array(z.string().min(1)).min(1).optional(),
      range: audienceRangeSchema.optional(),
    })
    .refine((op) => (op.selected_options === undefined) !== (op.range === undefined), {
      message: 'a filter gives exactly one of selected_options and range',
    }),
  z
    .strictObject({
      op: z.literal('change_filter'),
      filter_id: z.uuid(),
      mode: audienceFilterModeSchema.optional(),
      selected_options: z.array(z.string().min(1)).min(1).optional(),
      range: audienceRangeSchema.optional(),
    })
    .refine((op) => op.selected_options === undefined || op.range === undefined, {
      message: 'a filter change gives at most one of selected_options and range',
    }),
  z.strictObject({ op: z.literal('remove_filter'), filter_id: z.uuid() }),
  z.strictObject({
    op: z.literal('add_screener'),
    text: words(MAX_SCREENER_TEXT_CHARS),
    options: z.array(screenerOptionDraftSchema).min(1),
  }),
  z.strictObject({
    op: z.literal('recast_screener'),
    screener_id: z.uuid(),
    options: z.array(screenerOptionDraftSchema).min(1),
  }),
  z.strictObject({ op: z.literal('remove_screener'), screener_id: z.uuid() }),
  z.strictObject({ op: z.literal('add_limitation'), text: words(MAX_TARGETING_GAP_CHARS) }),
  z.strictObject({ op: z.literal('remove_limitation'), limitation_id: z.uuid() }),
]);
export type AudienceReshapeOp = z.infer<typeof audienceReshapeOpSchema>;

/** What a reshape asks for: the changes, in order, and what the researcher asked, in a sentence. */
export const audienceReshapeInputSchema = z.strictObject({
  ops: z.array(audienceReshapeOpSchema).min(1).max(MAX_AUDIENCE_RESHAPE_OPS),
  note: words(MAX_AUDIENCE_NOTE_CHARS),
});
export type AudienceReshapeInput = z.infer<typeof audienceReshapeInputSchema>;

/**
 * A filter with what it selects: the range when one is given, and the options otherwise. The
 * schemas above say which of the two a request may give; this only builds the shape.
 */
const withSelection = (
  base: Pick<AudienceFilter, 'id' | 'dimension' | 'mode'>,
  selection: { selected_options?: readonly string[]; range?: AudienceRange },
): AudienceFilter =>
  selection.range === undefined
    ? { ...base, selected_options: [...(selection.selected_options ?? [])] }
    : { ...base, range: { lower: selection.range.lower, upper: selection.range.upper } };

const sortedCountries = (countries: readonly Country[]): Country[] => [
  ...new Set(countries.toSorted()),
];

const sameTexts = (left: readonly { text: string }[], right: readonly { text: string }[]) =>
  left.length === right.length && left.every((item, index) => item.text === right[index]?.text);

/**
 * The stored audience with the requested changes applied in order.
 *
 * Everything no change names is carried over exactly as it is stored — each filter, each
 * screener's wording, ids and whose words it is in, and the Limitations — so a reshape cannot
 * reword or re-id anything by accident, and a filter change leaves a targeting gap it may have
 * closed in place until a `remove_limitation` in the same reshape says so. New filters, screeners,
 * options and gaps get ids from `mintId`, never from the request, and are in Sigora's words. The
 * pipeline constraints are the store's to derive.
 *
 * Refused, in order: `audience_filter_not_found` / `audience_screener_not_found` /
 * `audience_limitation_not_found` as a change is applied, then `audience_too_many_screeners` and
 * `audience_screener_invalid` for the result's screeners, then `audience_below_quality_floor`.
 */
export const planAudienceReshape = ({
  current,
  ops,
  mintId,
}: {
  current: AudienceContent;
  ops: readonly AudienceReshapeOp[];
  mintId: () => string;
}): AudienceContent => {
  let content: AudienceContent = current;

  for (const op of ops) {
    switch (op.op) {
      case 'set_count': {
        content = { ...content, target_completes: op.target_completes };
        break;
      }
      case 'set_countries': {
        content = { ...content, countries: sortedCountries(op.countries) };
        break;
      }
      case 'set_description': {
        content = { ...content, description: op.text };
        break;
      }
      case 'add_filter': {
        const filter = withSelection({ id: mintId(), dimension: op.dimension, mode: op.mode }, op);
        content = { ...content, filters: [...content.filters, filter] };
        break;
      }
      case 'change_filter': {
        if (!content.filters.some((filter) => filter.id === op.filter_id)) {
          throw filterNotFound(op.filter_id);
        }

        content = {
          ...content,
          filters: content.filters.map((filter) =>
            filter.id === op.filter_id
              ? withSelection(
                  {
                    id: filter.id,
                    dimension: filter.dimension,
                    mode: op.mode ?? filter.mode,
                  },
                  op.selected_options === undefined && op.range === undefined ? filter : op,
                )
              : filter,
          ),
        };
        break;
      }
      case 'remove_filter': {
        if (!content.filters.some((filter) => filter.id === op.filter_id)) {
          throw filterNotFound(op.filter_id);
        }

        content = {
          ...content,
          filters: content.filters.filter((filter) => filter.id !== op.filter_id),
        };
        break;
      }
      case 'add_screener': {
        const screener: AudienceScreener = {
          id: mintId(),
          text: op.text,
          options: op.options.map((option) => ({
            id: mintId(),
            text: option.text,
            terminates: option.terminates,
          })),
          worded_by: 'sigora',
        };
        content = { ...content, screeners: [...content.screeners, screener] };
        break;
      }
      case 'recast_screener': {
        const screener = content.screeners.find((each) => each.id === op.screener_id);
        if (screener === undefined) {
          throw screenerNotFound(op.screener_id);
        }

        const keep = new Map(screener.options.map((option) => [option.text, option.id]));
        const recast: AudienceScreener = {
          id: screener.id,
          text: screener.text,
          options: op.options.map((option) => ({
            id: keep.get(option.text) ?? mintId(),
            text: option.text,
            terminates: option.terminates,
          })),
          // One badge covers the question and its options, so options Sigora wrote over the
          // researcher's make the whole question Sigora's again. Options kept word for word — only
          // what they do changed — leave the researcher's wording as it was.
          worded_by: sameTexts(op.options, screener.options) ? screener.worded_by : 'sigora',
        };
        content = {
          ...content,
          screeners: content.screeners.map((each) => (each.id === recast.id ? recast : each)),
        };
        break;
      }
      case 'remove_screener': {
        if (!content.screeners.some((screener) => screener.id === op.screener_id)) {
          throw screenerNotFound(op.screener_id);
        }

        content = {
          ...content,
          screeners: content.screeners.filter((screener) => screener.id !== op.screener_id),
        };
        break;
      }
      case 'add_limitation': {
        const gap: TargetingGap = { id: mintId(), text: op.text };
        content = {
          ...content,
          limitations: {
            ...content.limitations,
            targeting_gaps: [...content.limitations.targeting_gaps, gap],
          },
        };
        break;
      }
      case 'remove_limitation': {
        if (!content.limitations.targeting_gaps.some((gap) => gap.id === op.limitation_id)) {
          throw limitationNotFound(op.limitation_id);
        }

        content = {
          ...content,
          limitations: {
            ...content.limitations,
            targeting_gaps: content.limitations.targeting_gaps.filter(
              (gap) => gap.id !== op.limitation_id,
            ),
          },
        };
        break;
      }
    }
  }

  assertAudienceWritable(content);

  return content;
};

const proposedGapSchema = z.object({ id: z.uuid().optional(), text: z.string() });

/**
 * The audience as a model writes it whole, through `update_artifact` for a first draft: the
 * stored shape without whose words a screener is in or the pipeline constraints, every id optional
 * but a filter's, and lenient about anything else it adds, which is ignored. It accepts the
 * version-1 shape too — `caveats` and no countries or screeners — and the Limitations either as
 * `targeting_gaps` or under `limitations`, as a stored audience reads. Only ever converted with
 * `toAudienceContentV2`, which decides the rest on the server.
 */
export const audienceProposalSchema = z.object({
  description: z.string(),
  target_completes: z.int(),
  countries: z.array(countrySchema).optional(),
  filters: z.array(
    z.object({
      id: z.uuid(),
      dimension: z.string(),
      mode: audienceFilterModeSchema,
      selected_options: z.array(z.string()).optional(),
      range: z.object({ lower: z.number(), upper: z.number() }).optional(),
    }),
  ),
  screeners: z
    .array(
      z.object({
        id: z.uuid().optional(),
        text: z.string(),
        options: z.array(
          z.object({ id: z.uuid().optional(), text: z.string(), terminates: z.boolean() }),
        ),
      }),
    )
    .optional(),
  targeting_gaps: z.array(proposedGapSchema).optional(),
  limitations: z.object({ targeting_gaps: z.array(proposedGapSchema) }).optional(),
  caveats: z.array(z.string()).optional(),
});
export type AudienceProposal = z.infer<typeof audienceProposalSchema>;

/**
 * Parses a model's audience, refusing one that is not an audience as `invalid_content` so the model
 * reads what was wrong with it.
 */
export const parseAudienceProposal = (content: unknown): AudienceProposal => {
  const parsed = audienceProposalSchema.safeParse(content);
  if (!parsed.success) {
    throw invalidAudience(`That is not an audience: ${z.prettifyError(parsed.error)}`, {
      issues: z.prettifyError(parsed.error),
    });
  }

  return parsed.data;
};

/**
 * A proposed filter in the stored shape: its options or its range, refusing one that gives both or
 * neither, or a range that is not two whole numbers from zero up with the lower first, as
 * `invalid_content` so the model reads which filter it was.
 */
const toProposedFilter = (filter: AudienceProposal['filters'][number]): AudienceFilter => {
  if ((filter.selected_options === undefined) === (filter.range === undefined)) {
    throw invalidAudience(
      `Filter ${filter.id} gives ${filter.range === undefined ? 'neither' : 'both'} selected_options ${filter.range === undefined ? 'nor' : 'and'} range; a filter gives exactly one.`,
      { filter_id: filter.id },
    );
  }

  if (filter.range !== undefined && !audienceRangeSchema.safeParse(filter.range).success) {
    throw invalidAudience(
      `Filter ${filter.id} gives the range ${String(filter.range.lower)}–${String(filter.range.upper)}; a range is two whole numbers from zero up, the lower first.`,
      { filter_id: filter.id },
    );
  }

  return withSelection({ id: filter.id, dimension: filter.dimension, mode: filter.mode }, filter);
};

/** Every sentence a pipeline constraint can read as, which a targeting gap never repeats. */
const PIPELINE_CONSTRAINT_TEXTS = new Set<string>([
  LANGUAGE_LIMITATION,
  SIMULATED_PARTICIPANTS_LIMITATION,
  countriesLimitation(['UK']),
  countriesLimitation(['US']),
  countriesLimitation(['UK', 'US']),
]);

/**
 * A proposed audience as the store writes it: ids, whose words each screener is in and which
 * Limitations are Sigora's to record, decided here.
 *
 * A screener whose id is in the current audience with the same question and option wording keeps
 * that screener's `worded_by` and option ids, so a whole-document rewrite leaves the researcher's
 * wording marked as theirs; anything else is Sigora's, and an option keeps the id of the current
 * option it is worded as. Countries default to both. A targeting gap worded as a current one keeps
 * its id; caveats become targeting gaps when no gaps are given; a gap that repeats a pipeline
 * constraint is dropped, since the store states those itself. A gap given with an id keeps it, so
 * a reworded gap stays the gap it was even when its new words are another gap's; a gap given
 * without one takes the id of the current gap worded as it is, unless another gap was given that
 * id. Ids the proposal leaves out come from `mintId`. The result still goes through the strict content parse, the structure rules and the
 * floor at the store.
 */
export const toAudienceContentV2 = (
  proposal: AudienceProposal,
  current: AudienceContent | null,
  mintId: () => string,
): AudienceContent => {
  const storedScreeners = new Map(
    (current?.screeners ?? []).map((screener) => [screener.id, screener]),
  );

  const screeners: AudienceScreener[] = (proposal.screeners ?? []).map((screener) => {
    const before = screener.id === undefined ? undefined : storedScreeners.get(screener.id);
    const kept =
      before !== undefined &&
      before.text === screener.text &&
      sameTexts(before.options, screener.options);
    const beforeIds = new Map((before?.options ?? []).map((option) => [option.text, option.id]));

    return {
      id: screener.id ?? mintId(),
      text: screener.text,
      options: screener.options.map((option, index) => ({
        id: kept
          ? (before.options[index]?.id ?? mintId())
          : (beforeIds.get(option.text) ?? option.id ?? mintId()),
        text: option.text,
        terminates: option.terminates,
      })),
      worded_by: kept ? before.worded_by : 'sigora',
    };
  });

  const storedGaps = new Map(
    (current?.limitations.targeting_gaps ?? []).map((gap) => [gap.text, gap.id]),
  );
  const proposedGaps =
    proposal.targeting_gaps ??
    proposal.limitations?.targeting_gaps ??
    (proposal.caveats ?? []).map((text) => ({ id: undefined, text }));
  const seen = new Set<string>();
  const given = new Set(proposedGaps.flatMap((gap) => (gap.id === undefined ? [] : [gap.id])));
  const targetingGaps: TargetingGap[] = proposedGaps.flatMap((gap) => {
    const text = gap.text.trim();
    if (PIPELINE_CONSTRAINT_TEXTS.has(text) || seen.has(text)) {
      return [];
    }
    seen.add(text);
    const wordedAs = storedGaps.get(text);
    const id =
      gap.id ?? (wordedAs !== undefined && !given.has(wordedAs) ? wordedAs : undefined) ?? mintId();
    given.add(id);

    return [{ id, text }];
  });

  const limitations: AudienceLimitations = {
    targeting_gaps: targetingGaps,
    pipeline_constraints: [],
  };

  return {
    description: proposal.description,
    target_completes: proposal.target_completes,
    countries: sortedCountries(proposal.countries ?? [...COUNTRIES]),
    filters: proposal.filters.map(toProposedFilter),
    screeners,
    limitations,
  };
};

/**
 * What produced a whole audience an agent wrote: a first draft when there was none, and otherwise
 * an update for the change above it, which is the only other whole audience an agent writes.
 */
export const agentAudienceWriteReason = (hadAudience: boolean): ArtifactVersionReason =>
  hadAudience ? 'updated_for_changed_guide' : 'drafted_from_guide';

/**
 * The line an audience version's reason reads as, on the version line and in the history, with
 * `notes` the version's change notes, which a reconciled version's line is read from. A version with
 * no reason reads as nothing.
 */
export const audienceVersionReasonText = (
  reason: ArtifactVersionReason | null,
  notes: ArtifactChangeNotes | null = null,
): string => {
  switch (reason) {
    case 'drafted_from_guide': {
      return 'Drafted from your guide';
    }
    case 'updated_for_changed_guide': {
      return 'Updated for your changed guide';
    }
    case 'updated_for_changed_questions': {
      return 'Updated for your changed research questions';
    }
    case 'redrafted_from_changed_goal': {
      return 'Redrafted from your changed goal';
    }
    case 'reconciled': {
      return reconciledVersionText(notes);
    }
    case 'changed_at_your_request': {
      return 'Changed at your request';
    }
    case 'added_at_your_request': {
      return 'Added at your request';
    }
    case 'removed_at_your_request': {
      return 'Removed at your request';
    }
    case 'reworded_by_you': {
      return 'Screener reworded by you';
    }
    // Reasons that say what was done to research questions or a guide, which no audience version is.
    case 'drafted_from_goal':
    case 'drafted_from_questions':
    case 'redrafted_from_changed_questions':
    case 'redrafted_for_coverage':
    case 'reworded_at_your_request':
    case 'reordered_at_your_request':
    case 'image_shown_at_your_request':
    case 'image_removed_at_your_request':
    case 'image_removed_from_materials':
    // Brief reasons: the store never writes one on an audience.
    case 'hidden_by_you':
    case 'shown_by_you':
    case 'reordered_by_you':
    case 'revised_by_sigora':
    case 'updated_from_follow_up':
    case null: {
      return '';
    }
  }
};

/** The refusal of an audience approval over a guide nobody has approved as it stands. */
export const DG_NOT_APPROVED_REFUSAL = 'Approve the guide first; the audience is built on it.';
